import prepareBreadcrumbStructuredPageData from "@insite/client-framework/Common/Utilities/prepareBreadcrumbStructuredPageData";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { generateLinksFrom } from "@insite/content-library/Components/PageBreadcrumbs";
import * as React from "react";
import { useEffect } from "react";
import { connect } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const currentPageData = getCurrentPage(state);
    const websiteSettings = getSettingsCollection(state).websiteSettings;

    return {
        breadcrumbLinks: state.components.breadcrumbs.links,
        linksState: state.links,
        nodeId: currentPageData?.nodeId,
        pageType: currentPageData?.type,
        homePageUrl: getHomePageUrl(state),
        enableBreadcrumbStructuredData: websiteSettings?.enableBreadcrumbStructuredData,
        website: state.context?.website,
    };
};

type Props = ReturnType<typeof mapStateToProps>;

const CustomBreadcrumbSchema: React.FC<Props> = (props: Props) => {
    const { breadcrumbLinks, linksState, enableBreadcrumbStructuredData, website, nodeId, homePageUrl, pageType } =
        props;

    useEffect(() => {
        if (typeof window === "undefined" || typeof document === "undefined") {
            return;
        }

        const scriptId = "breadcrumb-structured-data";

        // 1. Guard Clauses: Ensure features/settings are active
        if (!website || !enableBreadcrumbStructuredData) {
            return;
        }

        // 2. Main Check: Check if Breadcrumb Schema is already present on the page
        const schemaAlreadyExists = document.getElementById(scriptId);
        if (schemaAlreadyExists) {
            return;
        }

        // 3. Resolve Breadcrumb Links
        const links = breadcrumbLinks || generateLinksFrom(linksState, nodeId, homePageUrl);
        if (!links || links.length === 0) {
            return;
        }

        // 4. Generate structured data markup
        const breadCrumbsStructures = prepareBreadcrumbStructuredPageData(links);
        if (!breadCrumbsStructures || !breadCrumbsStructures.trim()) {
            return;
        }

        // 5. Inject the script element
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = scriptId;
        script.innerHTML = breadCrumbsStructures;
        document.head.appendChild(script);

        // Cleanup: Remove the injected script when component unmounts or deps change
        return () => {
            const injectedScript = document.getElementById(scriptId);
            if (injectedScript) {
                document.head.removeChild(injectedScript);
            }
        };
    }, [breadcrumbLinks, linksState, nodeId, homePageUrl, enableBreadcrumbStructuredData, website, pageType]);

    // Always return null safely here at the base rendering layer
    return null;
};

const definition: WidgetDefinition = {
    group: "Basic",
    displayName: "Custom Breadcrumb Schema",
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CustomBreadcrumbSchema),
    definition,
};

export default widgetModule;
